<template>
    <form :method="method" :action="action" @submit.prevent="submit()">
        <!-- Prevent using the enter key to submit the form -->
        <button v-if="preventEnterSubmit" type="submit" disabled style="display: none" aria-hidden="true"></button>

        <flash-message v-if="enableValidationMessage && hasLaravelErrors" type="alert-warning">{{ validationMessage }}</flash-message>

        <slot 
        :state="state" 
        :store="store" 
        :errors="error" 
        :submit="submit" 
        :loading="loading" 
        :pageLoaded="pageLoaded" 
        :hide="hide" 
        :response="response"
        :update="update" 
        :userInputs="userInputs"></slot>

        <div class="alert alert-danger tw-mt-2 tw-mb-0" v-show="serverError && !disableServerErrors">Sorry, an error has occurred</div>

        <div class="alert alert-warning tw-mt-2 tw-mb-0" v-show="!loading && (enableValidationMessage && Object.values(error.errors).length > 0)">Invalid fields, please review and try again</div>
    </form>
</template>

<script>
class ErrorContainer {
    constructor() {
        this.errors = {};
    }

    has(field) {
        return this.errors[field];
    }

    get(field) {
        return this.errors[field] ? this.errors[field] : []; 
    }

    clear(field, vm) {
        if(this.errors[field]) {
            vm.$delete(this.errors, field);
        }
    }

    clearAll() {
        this.errors = {};
    }
}

export default {
    name: "BaseForm",

    props: {
        formFields: {
            type: Object,
            default: function() {
                return {};
            }
        },
        ajax: {
            type: Boolean,
            default: false
        },
        refreshOnSuccess: {
            type: Boolean,
            default: false
        },
        onSuccess: {
            type: String,
            default: null
        },
        action: {
            type: String,
            required: true
        },
        method: {
            type: String,
            default: 'POST'
        },
        disableServerErrors: {
            type: Boolean,
            default: false
        },
        errorScroll: {
            type: Boolean,
            default: false
        },
        toastError: {
            type: Boolean,
            default: false
        },
        toastPosition: {
            type: String,
            default: 'top-center'
        },
        enableValidationMessage: {
            type: Boolean,
            default: false
        },
        validationMessage: {
            type: String,
            default: 'Changes were not submitted. Fix the highlighted fields and try again.'
        },
        laravelErrors: {
            type: Object,
            default: function() {
                return {}
            }
        },
        continueLoad: {
            type: Boolean,
            default: false
        },
        preventDoubleClick: {
            type: Boolean,
            default: true
        },
        preventEnterSubmit: {
            type: Boolean,
            default: true
        },
        stateClearable:{
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            state: {},
            store: {},
            controls: {},
            error: new ErrorContainer,
            serverError: false,
            loading: false,
            pageLoaded: false,
            response: null,
            initialState: {},
        }
    },

    computed: {
        errors() {
            return this.error.errors;
        },

        hasLaravelErrors() {
            return Object.keys(this.laravelErrors).length > 0;
        },
    },

    watch: {
        errors: {
            handler() {
                // On any update to the errors object, resend the errors to their corresponding form controls to make sure everything is up to date
                this.$nextTick(() => {
                    for(let control of Object.values(this.controls)) {
                        this.setErrors(control);
                    }
                })
            },
            deep: true
        }
    },

    created() {
        for(let [field, value] of Object.entries(this.formFields)) {
            this.$set(this.state, field, value);
        }
    },

    mounted() {
        // this.findFormControls(this);

        if(this.errorScroll) {
            window.addEventListener('load', () => {
                this.pageLoaded = true;

                let errorKeys = Object.keys(this.laravelErrors);
                let controlKeys = Object.keys(this.controls);
    
                if(errorKeys.length > 0) {
                    for(let x = 0; x < controlKeys.length; x++) {
                        if(errorKeys.includes(controlKeys[x])) {
                            this.controls[controlKeys[x]].$el.scrollIntoView({behavior: 'smooth'});
                            break;
                        }
                    }
                }
            });
        }

        this.$on('clear-error', name => {
            this.error.clear(name, this);
        });

        if(this.stateClearable){
            EventBus.$on('clear-state', this.clearState);
        }
    },

    methods: {
        // findFormControls(component) {
        //     for(let child of component.$children) {
        //         if(child.$options._componentTag == 'form-control') {
        //             if(child.$options.propsData.name != null) {
        //                 this.controls[child.$options.propsData.name] = child;
        //             }
                    
        //             if(child.$options.propsData.name != null && child.$options.propsData.initial != null) {
        //                 console.log('base-form found control: ' + child.$options.propsData.name);
        //                 this.$set(this.state, child.$options.propsData.name, child.$options.propsData.initial)
        //             }
        //         } else {
        //             this.findFormControls(child);
        //         }
        //     }
        // },

        submit(action = this.action, methodOverride = null) {
            // Prevent additional submissions if the component has already submitted
            if(this.loading && this.preventDoubleClick) {
                return;
            }

            this.loading = true;
            
            if(this.ajax) {
                
                // Grab all fields from the form
                let formData = new FormData(this.$el);
                
                // Make the Ajax request
                let method = this.method.toLowerCase();
                if(method != 'get' || method != 'post') {
                    if(!formData.has('_method')) {
                        formData.append('_method', method.toUpperCase());
                    }

                    method = 'post';
                }

                axios({
                    method: method,
                    url: action,
                    data: formData
                })
                .then(response => {
                    console.log('success', response);

                    this.response = response;
                    if(this.refreshOnSuccess){
                        location.reload();
                    }

                    // Store additional data
                    if(response.data.store) {
                        for(let [key, value] of Object.entries(response.data.store)) {
                            this.$set(this.store, key, value);
                        }
                    }

                    // Stop loading here so other components can't affect it when the events are emitted below
                    if(!this.refreshOnSuccess && !this.continueLoad) {
                        this.loading = false;
                    }

                    // Local event
                    this.$emit('success', response.data);
                    

                    // Emit an event so other components can respond
                    if(this.onSuccess) {
                        // Event Bus event
                        EventBus.$emit(this.onSuccess, response.data);
                    }
                })
                .catch(error => {
                    if(error.response.status == 422) {
                        console.log(error.response);

                        let errors = error.response.data.errors;
                        this.error.errors = errors;

                        if(this.errorScroll) {
                            for(let [name, control] of Object.entries(this.controls)) {
                                if(errors[name]) {
                                    control.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
                                    break;
                                }
                            }
                        }

                        this.loading = false;
                    } else {
                        if(this.toastError) {
                            this.$toasted.clear();
                            this.$toasted.error('An error has occurred', 
                            { 
                                position: this.toastPosition,
                                action: {
                                    icon: 'fa fa-times',
                                    class: 'tw-text-white tw-text-lg',
                                    onClick : (e, toastObject) => {
                                        toastObject.goAway(0);
                                    }
                                }
                            });
                        } else {
                            this.serverError = true;
                        }
                    }
                })
                .then(() => {
                    if(!this.refreshOnSuccess && !this.continueLoad) {
                        this.loading = false;
                    }
                })
            } else {

                this.$el.action = action;
                
                if(methodOverride) {
                    
                    let methodElement = this.$el.querySelector("input[name='_method']");
                    methodElement.value = methodOverride;
                    
                }
                
                this.$nextTick(() => {
                    this.$el.submit();
                });
                
            }
            
        },

        setErrors(control) {
            let errors = this.error.errors;

            // Compare the attribute names of each returned error to the regex defined on the form-control
            // Return an array of unique keys that match the regex
            let matchingKeys = Object.keys(errors).filter(key => {
                for(let regex of control.receiveErrorsFor) {
                    if(key.match(regex)) {
                        return true;
                    }
                }

                return false;
            });

            matchingKeys = [...new Set(matchingKeys)];

            // Now create a single array containing the actual error strings from each of the matching keys
            let formControlErrors = [];

            for(let [errorKey, errorStrings] of Object.entries(errors)) {
                if(matchingKeys.includes(errorKey)) {
                    formControlErrors.push(errorStrings);
                }
            }

            // Send the errors to the form-control
            control.setErrors(formControlErrors.flat());
        },

        hide(fields) {
            for(let field of fields) {
                if(this.controls[field]) {
                    this.controls[field].toggleErrors(false);
                }
            }
        },

        update(field, value) {
            if(this.state[field]) {
                this.state[field] = value;
            }else{
                this.$set(this.state, field, value);
            }

        },

        userInputs(){
            let array = [
            'PIA', 'insurance', 'agent', 'koala', 'dance', 'in', 'jumpsuit', 'jumpsuits', 
            'da#nce', 'jumpsUit3','horse', 'hor9se', 'correct', 'c^orrect', 'battery', 
            'batterY', 'staple', 'c^orrect hor9se batterY staple', 'dance koalas in jumpsuits'
            ];

            this.state.CICID ? array = array.concat(this.state.CICID.split(' ')) : "";
            this.state.CICID ? array = array.concat(this.state.CICID) : "";
            this.state.addressType ? array = array.concat(this.state.addressType.split(' ')) : "";
            this.state.addressType ? array = array.concat(this.state.addressType) : "";
            this.state.areaCodeCell ? array = array.concat(this.state.areaCodeCell.split(' ')) : "";
            this.state.areaCodeCell ? array = array.concat(this.state.areaCodeCell) : "";
            this.state.areaCodeOffice ? array = array.concat(this.state.areaCodeOffice.split(' ')) : "";
            this.state.areaCodeOffice ? array = array.concat(this.state.areaCodeOffice) : "";
            this.state.digit7NumCell ? array = array.concat(this.state.digit7NumCell.split(' ')) : "";
            this.state.digit7NumCell ? array = array.concat(this.state.digit7NumCell) : "";
            this.state.digit7NumOffice ? array = array.concat(this.state.digit7NumOffice.split(' ')) : "";
            this.state.digit7NumOffice ? array = array.concat(this.state.digit7NumOffice) : "";
            this.state.firstName ? array = array.concat(this.state.firstName.split(' ')) : "";
            this.state.firstName ? array = array.concat(this.state.firstName) : "";
            this.state.lastName ? array = array.concat(this.state.lastName.split(' ')) : "";
            this.state.lastName ? array = array.concat(this.state.lastName) : "";
            this.state.nickname ? array = array.concat(this.state.nickname.split(' ')) : "";
            this.state.nickname ? array = array.concat(this.state.nickname) : "";
            this.state.npn ? array = array.concat(this.state.npn.split(' ')) : "";
            this.state.npn ? array = array.concat(this.state.npn) : "";
            this.state.title ? array = array.concat(this.state.title.split(' ')) : "";
            this.state.title ? array = array.concat(this.state.title) : "";
            this.state.personalEmail ? array = array.concat(this.state.personalEmail.split(' ')) : "";
            this.state.personalEmail ? array = array.concat(this.state.personalEmail) : "";
            this.state.businessEmail ? array = array.concat(this.state.businessEmail.split(' ')) : "";
            this.state.businessEmail ? array = array.concat(this.state.businessEmail) : "";
            this.state.line1 ? array = array.concat(this.state.line1.split(' ')) : "";
            this.state.line1 ? array = array.concat(this.state.line1) : "";
            this.state.line2 ? array = array.concat(this.state.line2.split(' ')) : "";
            this.state.line2 ? array = array.concat(this.state.line2) : "";
            this.state.city ? array = array.concat(this.state.city.split(' ')) : "";
            this.state.city ? array = array.concat(this.state.city) : "";
            this.state.zip_code ? array = array.concat(this.state.zip_code.split(' ')) : "";
            this.state.zip_code ? array = array.concat(this.state.zip_code) : "";
            array.push(' ');

            return array;
        },

        clearState(){
            //Object.keys(this.state).forEach(key => this.state[key] = this.initialState[key]);
            //this.$el.reset();
            console.log('clearing state');
            this.$nextTick(() => {
                Object.keys(this.state).forEach(key => this.state[key] = this.initialState[key]);
            });
            
        }

        /*
        participantInfo($participant){
            let array = [
            'PIA', 'insurance', 'agent', 'koala', 'dance', 'in', 'jumpsuit', 'jumpsuits', 
            'da#nce', 'jumpsUit3','horse', 'hor9se', 'correct', 'c^orrect', 'battery', 
            'batterY', 'staple', 'c^orrect hor9se batterY staple', 'dance koalas in jumpsuits'
            ];

            participant.CICID ? array = array.concat(participant.CICID) : "";
            this.state.addressType ? array = array.concat(this.state.addressType.split(' ')) : "";
            this.state.addressType ? array = array.concat(this.state.addressType) : "";
            this.state.areaCodeCell ? array = array.concat(this.state.areaCodeCell.split(' ')) : "";
            this.state.areaCodeCell ? array = array.concat(this.state.areaCodeCell) : "";
            this.state.areaCodeOffice ? array = array.concat(this.state.areaCodeOffice.split(' ')) : "";
            this.state.areaCodeOffice ? array = array.concat(this.state.areaCodeOffice) : "";
            this.state.digit7NumCell ? array = array.concat(this.state.digit7NumCell.split(' ')) : "";
            this.state.digit7NumCell ? array = array.concat(this.state.digit7NumCell) : "";
            this.state.digit7NumOffice ? array = array.concat(this.state.digit7NumOffice.split(' ')) : "";
            this.state.digit7NumOffice ? array = array.concat(this.state.digit7NumOffice) : "";
            participant.firstName ? array = array.concat(participant.firstName) : "";
            participant.lastName ? array = array.concat(participant.lastName) : "";
            participant.nickname ? array = array.concat(participant.nickname) : "";
            participant.npn ? array = array.concat(participant.npn) : "";
            participant.title ? array = array.concat(participant.title) : "";
            this.state.personalEmail ? array = array.concat(this.state.personalEmail) : "";
            participant.businessEmail ? array = array.concat(participant.businessEmail) : "";
            participant.line1 ? array = array.concat(participant.line1) : "";
            participant.line2 ? array = array.concat(participant.line2) : "";
            participant.city ? array = array.concat(participant.city) : "";
            participant.zip_code ? array = array.concat(participant.zip_code) : "";
            array.push(' ');

            return array;
        } */
    }

}
</script>

<style>

</style>